/**
 * Gestion de `window.halmya` en mode queue (pattern Segment / Intercom).
 *
 * Le snippet officiel posé sur la page du client peut être :
 *
 *   <script>
 *     window.halmya = window.halmya || function () {
 *       (window.halmya.q = window.halmya.q || []).push(arguments);
 *     };
 *   </script>
 *   <script async src="..."></script>
 *
 * À ce stade, `window.halmya` est une fonction qui empile les appels dans `q`.
 * Le loader doit :
 *  1. récupérer cette queue (en la créant si elle n'existe pas)
 *  2. continuer à empiler les nouveaux appels jusqu'à ce que le core soit chargé
 *  3. exposer la queue au core via le bootstrap context
 *
 * Le core, à son démarrage, rejouera la queue et remplacera `window.halmya`
 * par la vraie implémentation.
 */

import type { HalmyaQueue } from "../shared/types";

declare global {
  // eslint-disable-next-line no-var
  var halmya: HalmyaQueue | undefined;
}

/**
 * S'assure que `window.halmya` existe en mode queue.
 * Retourne le tableau de commandes accumulées.
 */
export function ensureQueue(): unknown[][] {
  const existing = window.halmya;

  if (typeof existing === "function" && Array.isArray(existing.q)) {
    return existing.q;
  }

  // Le snippet officiel n'a pas été posé : on installe une fonction queue
  // équivalente pour ne pas perdre les appels futurs.
  const queue: unknown[][] = [];
  const fn = function (...args: unknown[]): void {
    queue.push(args);
  } as HalmyaQueue;
  fn.q = queue;
  window.halmya = fn;

  return queue;
}
